import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Book, Map, Sparkles, Clock, Share2, Eye, EyeOff } from 'lucide-react';
import logger from '../utils/logger';

const Landing = ({ setIsAuthenticated, showAuth, setShowAuth }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [pricing, setPricing] = useState({
    paid_tier_price: 29.99,
    pdf_download_price: 3.00,
    free_tier_album_limit: 4,
    print_ship_base_price: 0.00
  });

  useEffect(() => {
    // Fetch pricing from admin settings
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Keep default pricing if fetch fails
      }
    };
    fetchPricing();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    logger.logAction(isLogin ? 'login_attempt' : 'register_attempt', { email: formData.email });

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      const response = await axios.post(`${API}${endpoint}`, payload);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      logger.logAction(isLogin ? 'login_success' : 'register_success', { email: formData.email });
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      setIsAuthenticated(true);
    } catch (error) {
      logger.error(isLogin ? 'Login failed' : 'Registration failed', error, { email: formData.email });
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/magic-link`, { email: formData.email });
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check the console/logs for the link (demo mode)');
    } catch (error) {
      toast.error('Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="flex justify-start">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-amber-200">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-900">AI-Powered Photo Albums</span>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight text-left">
                Your Memories,
                <br />
                <span className="bg-gradient-to-r from-amber-600 via-rose-500 to-blue-600 bg-clip-text text-transparent">
                  Beautifully Preserved
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 text-left">
                Link your social media, picture folders or upload all your images. We will create a custom photo album that can be shared online or printed.
              </p>

              <div className="flex gap-4 justify-start">
                <Button
                  data-testid="get-started-btn"
                  onClick={() => setShowAuth(true)}
                  size="lg"
                  className="bg-gradient-to-r from-amber-600 to-rose-500 text-white px-8 py-6 text-lg font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Get Started Free
                </Button>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-lift">
                <img 
                  src="https://images.unsplash.com/photo-1528569937393-ee892b976859?w=800&q=80" 
                  alt="Photo Album with Memories"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 border-2 border-amber-200">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">${pricing.paid_tier_price.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">per year</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Camera className="w-8 h-8" />}
            title="AI Photo Analysis"
            description="Automatically detect dates, locations, and people in your photos with advanced AI"
            gradient="from-amber-400 to-orange-500"
          />
          <FeatureCard
            icon={<Book className="w-8 h-8" />}
            title="Interactive Flip-Books"
            description="Create beautiful digital albums with smooth page-turning animations"
            gradient="from-rose-400 to-pink-500"
          />
          <FeatureCard
            icon={<Map className="w-8 h-8" />}
            title="Travel Maps"
            description="Visualize your journey with interactive maps showing your photo locations"
            gradient="from-blue-400 to-cyan-500"
          />
          <FeatureCard
            icon={<Clock className="w-8 h-8" />}
            title="Timeline View"
            description="Organize your memories chronologically with beautiful timeline displays"
            gradient="from-purple-400 to-violet-500"
          />
          <FeatureCard
            icon={<Share2 className="w-8 h-8" />}
            title="Easy Sharing"
            description="Share your albums with friends and family with a single link"
            gradient="from-green-400 to-emerald-500"
          />
          <FeatureCard
            icon={<Sparkles className="w-8 h-8" />}
            title="PDF Export"
            description="Download your albums as high-quality PDFs for printing or archiving"
            gradient="from-amber-400 to-yellow-500"
          />
        </div>
      </div>

      {/* Pricing Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Simple, Transparent Pricing</h2>
        <p className="text-center text-sm text-gray-600 mb-8">
          By using our service, you agree to our{' '}
          <a href="/terms-of-service.html" target="_blank" className="text-amber-600 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy.html" target="_blank" className="text-amber-600 hover:underline">
            Privacy Policy
          </a>
        </p>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <PricingCard
            title="Free"
            price="$0"
            features={[
              `Up to ${pricing.free_tier_album_limit} active albums`,
              'Unlimited photo uploads',
              'AI-powered captions',
              'Facial recognition training',
              'Share digital flipbooks',
              'Cloud storage linking'
            ]}
            buttonText="Get Started"
            onClick={() => setShowAuth(true)}
          />
          <PricingCard
            title="Premium"
            price={`$${pricing.paid_tier_price.toFixed(2)}/year`}
            features={[
              'Unlimited albums',
              'Unlimited photo uploads',
              'Advanced AI analysis',
              `Download 8x8 PDF albums ($${pricing.pdf_download_price.toFixed(2)} each)`,
              pricing.print_ship_base_price > 0 
                ? `Print & ship service (from $${pricing.print_ship_base_price.toFixed(2)})`
                : 'Print & ship service (pricing TBD)',
              'Priority support',
              'All Free features included'
            ]}
            buttonText="Upgrade Now"
            onClick={() => setShowAuth(true)}
            featured
          />
        </div>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent data-testid="auth-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{isLogin ? 'Welcome Back' : 'Create Account'}</DialogTitle>
            <DialogDescription>
              {isLogin ? 'Login to access your photo albums' : 'Start creating beautiful photo albums today'}
            </DialogDescription>
          </DialogHeader>
          <form data-testid="auth-form" onSubmit={showMagicLink ? handleMagicLink : handleSubmit} className="space-y-4">
            {magicLinkSent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                <p className="font-semibold mb-1">Magic link sent!</p>
                <p>Check the browser console or server logs for your magic link (demo mode). In production, this would be sent to your email.</p>
              </div>
            )}

            {!isLogin && !showMagicLink && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  data-testid="name-input"
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="email-input"
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            {!showMagicLink && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-rose-500 text-white"
              disabled={loading || magicLinkSent}
            >
              {loading ? 'Please wait...' : showMagicLink ? 'Send Magic Link' : isLogin ? 'Login' : 'Sign Up'}
            </Button>
            
            {isLogin && !magicLinkSent && (
              <div className="text-center">
                <button
                  data-testid="magic-link-toggle-btn"
                  type="button"
                  onClick={() => setShowMagicLink(!showMagicLink)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {showMagicLink ? '← Use password instead' : '✨ Use magic link (passwordless)'}
                </button>
              </div>
            )}
          </form>
          <div className="text-center">
            <button
              data-testid="toggle-auth-mode-btn"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, gradient }) => (
  <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 hover-lift">
    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${gradient} flex items-center justify-center text-white mb-4`}>
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const PricingCard = ({ title, price, features, buttonText, onClick, featured }) => (
  <div
    className={`rounded-2xl p-8 border-2 ${
      featured ? 'border-amber-500 bg-white shadow-2xl scale-105' : 'border-gray-200 bg-white/60 backdrop-blur-sm'
    } hover-lift`}
  >
    <div className="text-center mb-6">
      <h3 className="text-2xl font-bold mb-2">{title}</h3>
      <div className="text-4xl font-bold text-gray-900">
        {price}
      </div>
    </div>
    <ul className="space-y-3 mb-6">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
            <span className="text-green-600 text-xs">✓</span>
          </div>
          <span className="text-gray-700">{feature}</span>
        </li>
      ))}
    </ul>
    <Button
      onClick={onClick}
      className={`w-full ${
        featured ? 'bg-gradient-to-r from-amber-600 to-rose-500 text-white' : 'bg-gray-900 text-white'
      }`}
    >
      {buttonText}
    </Button>
  </div>
);

export default Landing;
